import { useDisplayType } from '@ds-mgnl/marketing';
import styles from './TopNav.module.css';
import { ButtonLink, StandaloneIcon, classNames } from '@dsx/react';
import type { ReactNode, RefObject } from 'react';
import {
	createRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import type { Logo, MenuContent, MenuLink } from './types';
import { TopNavPrimaryItem } from './TopNavPrimaryItem';
import { TopNavPrimaryItemMobile } from './TopNavPrimaryItemMobile';
import { TopNavBarMobile } from './TopNavBarMobile';
import { Link } from 'react-router-dom';
import { updateActiveContent } from './utils';

const renderDesktopCTA = (ctaContent: MenuLink[]) => {
	if (ctaContent.length === 0) {
		return null;
	}
	return (
		<div className={styles['top-nav-cta']}>
			{ctaContent.map(({ path, value, icon, label, openInNewTab }, index) => (
				<ButtonLink
					key={path}
					href={value}
					target={openInNewTab ? '_blank' : '_self'}
					rel="nofollow"
					variant={index == 0 ? 'primary' : 'default'}
					className={classNames(styles['menu-item-link'])}
					icon={icon}
				>
					{label}
				</ButtonLink>
			))}
		</div>
	);
};

type TopNavProps = {
	activePath: string;
	initMenuContent: MenuContent[];
	ctaLinks: MenuLink[];
	logo: Logo;
	header?: ReactNode;
};

const initializeMenuRefs = (
	refs: RefObject<HTMLDivElement>[],
	menuContent: MenuContent[],
) =>
	Array(menuContent.length)
		.fill(null)
		.map((_, i) => refs[i] || createRef());

const TopNav = ({
	activePath,
	initMenuContent,
	ctaLinks,
	header,
	logo,
}: TopNavProps) => {
	const displayType = useDisplayType();
	const isMinDesktop = displayType === 'desktop';

	const [open, setOpen] = useState(false);
	const [activeItemId, setActiveItemId] = useState<string>('');

	// Derive menu content from props instead of managing in state
	const menuContent = useMemo(
		() => updateActiveContent(initMenuContent, activePath),
		[initMenuContent, activePath],
	);

	const menuRef = useRef<HTMLUListElement>(null);
	const toggleButtonRef = useRef<HTMLDivElement>(null);

	const [toggleMenuRefs] = useState<RefObject<HTMLDivElement>[]>(
		initializeMenuRefs([], menuContent),
	);

	const handleMobileMenuToggled = () => {
		setOpen((currentState) => !currentState);
	};

	// TODO: consider other scenarios for accesibility
	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				if (
					isMinDesktop &&
					toggleMenuRefs.every(
						(ref) => ref.current && !ref.current.contains(event.target as Node),
					)
				) {
					setActiveItemId('');
					setOpen(false);
				} else if (
					toggleButtonRef.current &&
					!toggleButtonRef.current.contains(event.target as Node)
				) {
					setOpen(false);
				}
			}
		},
		[isMinDesktop, toggleMenuRefs],
	);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	return (
		<header className={styles['top-nav']} key={activePath}>
			<div className={styles['top-nav-content']}>
				<div className={styles['top-nav-logo']}>
					<a href={logo.href}>
						<img
							src={logo.svg}
							alt={logo.alt}
							className={styles['logo']}
							width={logo.width}
							height={logo.height}
						/>
					</a>
				</div>

				{isMinDesktop ? (
					<div className={styles['top-nav-primary-content']}>
						<div className={styles['top-nav-primary-menu']}>
							{menuContent.map((item, index) => (
								<TopNavPrimaryItem
									ref={toggleMenuRefs[index]}
									menuRef={menuRef}
									key={item.primary.path}
									menuItem={item}
									activeItemId={activeItemId}
									onSelect={(value: string) => {
										setActiveItemId(value);
										setOpen(value !== ''); // if a non-empty value came back, the menu should be open
									}}
									isOpen={open && activeItemId == item.primary.path}
								/>
							))}
						</div>
						{header && <div className={styles['top-nav-header']}>{header}</div>}
						{renderDesktopCTA(ctaLinks)}
					</div>
				) : (
					<TopNavBarMobile
						toggleButtonRef={toggleButtonRef}
						isOpen={open}
						onSelect={handleMobileMenuToggled}
					/>
				)}
			</div>
			{!isMinDesktop && open && (
				<ul ref={menuRef} className={styles['top-nav-mobile-menu']}>
					{menuContent.map((mc) => (
						<TopNavPrimaryItemMobile key={mc.primary.path} item={mc} />
					))}
					{ctaLinks.map((ctaLink, index) => (
						<li
							key={ctaLink.path}
							className={classNames(
								!!menuContent.length && index == 0 ? styles['first-cta'] : '',
							)}
						>
							<Link
								to={ctaLink.value}
								target={ctaLink.openInNewTab ? '_blank' : '_self'}
								className={styles['mobile-cta-link']}
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									gap: '4px',
								}}
							>
								{ctaLink.icon && (
									<StandaloneIcon title={ctaLink.label} icon={ctaLink.icon} />
								)}
								{ctaLink.label}
							</Link>
						</li>
					))}
				</ul>
			)}
		</header>
	);
};

export { TopNav };
